import React, { useState } from 'react';
import { FaSpinner, FaMagic, FaUser, FaBriefcase, FaFileAlt, FaPalette } from 'react-icons/fa';
import { useGemini } from '../../hooks/useGemini';
import { toast } from 'react-hot-toast';

const templates = [
  { id: 'modern', name: 'Modern Professional', icon: '🎯' },
  { id: 'creative', name: 'Creative', icon: '🎨' },
  { id: 'traditional', name: 'Traditional', icon: '📝' },
  { id: 'minimal', name: 'Minimal', icon: '✨' }
];

const CoverLetterForm = ({ data, onUpdateData, onTemplateChange }) => {
  const [activeSection, setActiveSection] = useState('jobInfo');
  const { generateContent, loading: aiLoading } = useGemini();
  const [generatingSection, setGeneratingSection] = useState('');

  const handleInputChange = (section, field, value) => {
    onUpdateData(section, {
      ...data[section],
      [field]: value
    });
  };

  const generateCoverLetterContent = async (section) => {
    if (!data.jobInfo.companyName || !data.jobInfo.jobTitle || !data.jobInfo.jobDescription) {
      toast.error('Please fill in company name, job title, and job description first');
      return;
    }

    setGeneratingSection(section);
    
    try {
      let prompt = '';
      
      switch(section) {
        case 'introduction':
          prompt = `Generate a professional and engaging introduction paragraph for a cover letter with the following details:
            Company: ${data.jobInfo.companyName}
            Position: ${data.jobInfo.jobTitle}
            Context: Express enthusiasm for the role and company, mention how you learned about the position.`;
          break;
          
        case 'body':
          prompt = `Generate the main body paragraphs for a cover letter with the following details:
            Company: ${data.jobInfo.companyName}
            Position: ${data.jobInfo.jobTitle}
            Job Description: ${data.jobInfo.jobDescription}
            Focus on: Relevant skills and experiences, specific achievements, and why you're a good fit for the role.`;
          break;
          
        case 'conclusion':
          prompt = `Generate a strong concluding paragraph for a cover letter with the following details:
            Company: ${data.jobInfo.companyName}
            Position: ${data.jobInfo.jobTitle}
            Include: Thank the reader, express interest in an interview, and provide a call to action.`;
          break;
        default:
          break;
      }

      const generatedContent = await generateContent(prompt);
      
      handleInputChange('content', section, generatedContent);
      toast.success('Generated new content!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to generate content. Please try again.');
    } finally {
      setGeneratingSection('');
    }
  };

  const sections = [
    { id: 'jobInfo', label: 'Job Information', icon: <FaBriefcase /> },
    { id: 'personalInfo', label: 'Personal Information', icon: <FaUser /> },
    { id: 'content', label: 'Letter Content', icon: <FaFileAlt /> },
    { id: 'template', label: 'Choose Template', icon: <FaPalette /> }
  ];

  return (
    <div className="space-y-6">
      <div className="border-b border-white/10">
        <nav className="-mb-px flex space-x-6">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`py-3 px-4 flex items-center gap-2 border-b-2 transition-all duration-200 ${
                activeSection === section.id
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'
              }`}
            >
              {section.icon}
              {section.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-6">
        {activeSection === 'jobInfo' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Company Name</label>
              <input
                type="text"
                value={data.jobInfo.companyName}
                onChange={(e) => handleInputChange('jobInfo', 'companyName', e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-white/10 text-white
                         focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50
                         placeholder-gray-500 transition-all duration-200"
                placeholder="Enter company name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Job Title</label>
              <input
                type="text"
                value={data.jobInfo.jobTitle}
                onChange={(e) => handleInputChange('jobInfo', 'jobTitle', e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-white/10 text-white
                         focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50
                         placeholder-gray-500 transition-all duration-200"
                placeholder="Enter job title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Hiring Manager (optional)</label>
              <input
                type="text"
                value={data.jobInfo.hiringManager}
                onChange={(e) => handleInputChange('jobInfo', 'hiringManager', e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-white/10 text-white
                         focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50
                         placeholder-gray-500 transition-all duration-200"
                placeholder="Enter hiring manager"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Company Address (optional)</label>
              <input
                type="text"
                value={data.jobInfo.companyAddress}
                onChange={(e) => handleInputChange('jobInfo', 'companyAddress', e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-white/10 text-white
                         focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50
                         placeholder-gray-500 transition-all duration-200"
                placeholder="Enter company address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Job Description</label>
              <textarea
                value={data.jobInfo.jobDescription}
                onChange={(e) => handleInputChange('jobInfo', 'jobDescription', e.target.value)}
                rows={4}
                className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-white/10 text-white
                         focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50
                         placeholder-gray-500 transition-all duration-200"
                placeholder="Paste the job description here..."
              />
            </div>
          </div>
        )}

        {activeSection === 'personalInfo' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Full Name</label>
              <input
                type="text"
                value={data.personalInfo.fullName}
                onChange={(e) => handleInputChange('personalInfo', 'fullName', e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-white/10 text-white
                         focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50
                         placeholder-gray-500 transition-all duration-200"
                placeholder="Enter your full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
              <input
                type="email"
                value={data.personalInfo.email}
                onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-white/10 text-white
                         focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50
                         placeholder-gray-500 transition-all duration-200"
                placeholder="Enter your email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Phone</label>
              <input
                type="tel"
                value={data.personalInfo.phone}
                onChange={(e) => handleInputChange('personalInfo', 'phone', e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-white/10 text-white
                         focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50
                         placeholder-gray-500 transition-all duration-200"
                placeholder="Enter your phone number"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Address</label>
              <input
                type="text"
                value={data.personalInfo.address}
                onChange={(e) => handleInputChange('personalInfo', 'address', e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-white/10 text-white
                         focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50
                         placeholder-gray-500 transition-all duration-200"
                placeholder="Enter your address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Date</label>
              <input
                type="date"
                value={data.personalInfo.date}
                onChange={(e) => handleInputChange('personalInfo', 'date', e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-white/10 text-white
                         focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50
                         placeholder-gray-500 transition-all duration-200"
                placeholder="Enter the date"
              />
            </div>
          </div>
        )}

        {activeSection === 'content' && (
          <div className="space-y-6">
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-300">Introduction</label>
                <button
                  onClick={() => generateCoverLetterContent('introduction')}
                  disabled={generatingSection === 'introduction'}
                  className="flex items-center gap-2 px-3 py-1 text-sm rounded-lg
                           bg-purple-600/10 text-purple-400 border border-purple-500/20
                           hover:bg-purple-600/20 hover:border-purple-500/30
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-200"
                >
                  {generatingSection === 'introduction' ? (
                    <FaSpinner className="animate-spin" />
                  ) : (
                    <FaMagic />
                  )}
                  Generate
                </button>
              </div>
              <textarea
                value={data.content.introduction}
                onChange={(e) => handleInputChange('content', 'introduction', e.target.value)}
                rows={4}
                className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-white/10 text-white
                         focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50
                         placeholder-gray-500 transition-all duration-200"
                placeholder="Write your introduction paragraph"
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-300">Body</label>
                <button
                  onClick={() => generateCoverLetterContent('body')}
                  disabled={generatingSection === 'body'}
                  className="flex items-center gap-2 px-3 py-1 text-sm rounded-lg
                           bg-purple-600/10 text-purple-400 border border-purple-500/20
                           hover:bg-purple-600/20 hover:border-purple-500/30
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-200"
                >
                  {generatingSection === 'body' ? (
                    <FaSpinner className="animate-spin" />
                  ) : (
                    <FaMagic />
                  )}
                  Generate
                </button>
              </div>
              <textarea
                value={data.content.body}
                onChange={(e) => handleInputChange('content', 'body', e.target.value)}
                rows={8}
                className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-white/10 text-white
                         focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50
                         placeholder-gray-500 transition-all duration-200"
                placeholder="Write your body paragraphs"
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-gray-300">Conclusion</label>
                <button
                  onClick={() => generateCoverLetterContent('conclusion')}
                  disabled={generatingSection === 'conclusion'}
                  className="flex items-center gap-2 px-3 py-1 text-sm rounded-lg
                           bg-purple-600/10 text-purple-400 border border-purple-500/20
                           hover:bg-purple-600/20 hover:border-purple-500/30
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-200"
                >
                  {generatingSection === 'conclusion' ? (
                    <FaSpinner className="animate-spin" />
                  ) : (
                    <FaMagic />
                  )}
                  Generate
                </button>
              </div>
              <textarea
                value={data.content.conclusion}
                onChange={(e) => handleInputChange('content', 'conclusion', e.target.value)}
                rows={4}
                className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-white/10 text-white
                         focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50
                         placeholder-gray-500 transition-all duration-200"
                placeholder="Write your conclusion paragraph"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Signature</label>
              <input
                type="text"
                value={data.content.signature}
                onChange={(e) => handleInputChange('content', 'signature', e.target.value)}
                className="w-full px-4 py-2 rounded-lg bg-gray-800/50 border border-white/10 text-white
                         focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50
                         placeholder-gray-500 transition-all duration-200"
                placeholder="Sincerely, [Your Name]"
              />
            </div>
          </div>
        )}

        {activeSection === 'template' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {templates.map(template => (
              <button
                key={template.id}
                onClick={() => onTemplateChange(template.id)}
                className={`p-4 rounded-lg border transition-all duration-200
                         flex items-center gap-3 ${
                  data.template === template.id
                    ? 'bg-blue-600/20 border-blue-500/50 text-blue-400'
                    : 'bg-gray-800/50 border-white/10 text-gray-300 hover:bg-gray-800/70'
                }`}
              >
                <span className="text-2xl">{template.icon}</span>
                <span className="font-medium">{template.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CoverLetterForm;
